import { useContext } from 'react';

import { Calendar as ReactBigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { useNavigate } from 'react-router-dom';
import styled, { ThemeContext } from 'styled-components';
import { format, getDay, getISOWeek, parse, startOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';

import 'react-big-calendar/lib/css/react-big-calendar.css';

const Calendar = ReactBigCalendar as unknown as React.JSX.ElementType;

import { GetDeliveriesQuery } from 'data/__generated__';

import { DeliveryCalendarEvent } from './components/DeliveryCalendarEvent';
import { getColorsFromState } from './constants/states';

const locales = { fr };

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

type DeliveryEvent = {
    id: string;
    title: string;
    start: Date;
    end: Date;
    allDay: boolean;
    delivery: GetDeliveriesQuery['deliveries'][number];
    backgroundColor: string;
};

type DeliveriesTimelineViewProps = {
    deliveries: GetDeliveriesQuery['deliveries'];
    selectedDeliveryIds: string[];
    toggleDeliverySelection: (deliveryId: string) => void;
};

export const DeliveriesTimelineView = ({
    deliveries,
    selectedDeliveryIds,
    toggleDeliverySelection,
}: DeliveriesTimelineViewProps) => {
    const theme = useContext(ThemeContext);
    const navigate = useNavigate();

    function mapDeliveryToEvent(delivery: GetDeliveriesQuery['deliveries'][number]): DeliveryEvent | null {
        if (!delivery.deliveryDate) {
            return null;
        }
        const date = new Date(delivery.deliveryDate);
        if (isNaN(date.getTime())) {
            return null;
        }

        const { backgroundColor } = getColorsFromState(delivery.state);
        return {
            id: delivery._id,
            title: delivery.organizationName || delivery.siteName || delivery.label || delivery._id,
            start: date,
            end: date,
            allDay: true,
            delivery,
            backgroundColor,
        };
    }

    const events: DeliveryEvent[] = deliveries
        .map(mapDeliveryToEvent)
        .filter((event): event is DeliveryEvent => event !== null);

    const components = {
        event: ({ event }: { event: DeliveryEvent }) => (
            <DeliveryCalendarEvent delivery={event.delivery} isSelected={selectedDeliveryIds.includes(event.id)} />
        ),
    };

    function handleSelectEvent(event: DeliveryEvent) {
        toggleDeliverySelection(event.id);
    }

    function handleDoubleClickEvent(event: DeliveryEvent) {
        navigate(`/delivery/${event.id}`);
    }

    return (
        <CalendarContainer>
            <Calendar
                localizer={localizer}
                events={events}
                eventPropGetter={() => ({
                    style: { backgroundColor: 'transparent' },
                })}
                dayPropGetter={(date: Date) => {
                    const today = new Date();
                    const isToday =
                        date.getDate() === today.getDate() &&
                        date.getMonth() === today.getMonth() &&
                        date.getFullYear() === today.getFullYear();
                    return {
                        style: {
                            backgroundColor: isToday ? theme?.backgroundColor : theme?.cardBackgroundColor,
                        },
                    };
                }}
                startAccessor="start"
                endAccessor="end"
                views={['month', 'week']}
                defaultView="week"
                formats={{
                    dayFormat: (date: Date) => format(date, 'EEEE dd MMM', { locale: fr }),
                    dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }) =>
                        `S${getISOWeek(start)} ${format(start, 'dd MMM', { locale: fr })} – ${format(end, 'dd MMM', { locale: fr })}`,
                }}
                components={components}
                onSelectEvent={handleSelectEvent}
                onDoubleClickEvent={handleDoubleClickEvent}
                style={{ height: '100%', width: '100%' }}
                culture="fr"
                messages={{
                    previous: 'Précédent',
                    next: 'Suivant',
                    today: "Aujourd'hui",
                    showMore: (total: number) => `+${total} de plus`,
                    week: 'Semaine',
                    month: 'Mois',
                }}
            />
        </CalendarContainer>
    );
};

const CalendarContainer = styled.div`
    flex: 1;
    overflow: auto;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    padding: 10px;

    .rbc-event {
        padding: 0;
        margin: 1px 0;
        background-color: transparent;
        border: none;
    }

    .rbc-event:focus {
        outline: none;
    }

    .rbc-show-more {
        font-size: 12px;
        font-weight: 600;
    }

    .rbc-time-view {
        display: flex;
        flex-direction: column;
        flex: 1;
    }

    .rbc-time-content {
        display: none;
    }

    .rbc-time-header {
        flex: 1;
        display: flex;
        flex-direction: column;
    }

    .rbc-time-header-content {
        border: none;
        flex: 1;
        display: flex;
        flex-direction: column;
    }

    .rbc-allday-cell {
        flex: 1;
        overflow-y: auto;
    }

    .rbc-row-content {
        min-height: 100%;
    }

    .rbc-toolbar {
        color: ${({ theme }) => theme.textColor};
    }

    .rbc-toolbar button {
        color: ${({ theme }) => theme.textColor};
        border-color: ${({ theme }) => theme.lightBorderColor};
    }

    .rbc-toolbar button:hover,
    .rbc-toolbar button:focus {
        background-color: ${({ theme }) => theme.backgroundColor};
        color: ${({ theme }) => theme.textColor};
    }

    .rbc-toolbar button.rbc-active {
        background-color: ${({ theme }) => theme.ctaPrimaryColor};
        color: ${({ theme }) => theme.ctaPrimaryTextColor};
        border-color: ${({ theme }) => theme.ctaPrimaryColor};
    }

    .rbc-header {
        color: ${({ theme }) => theme.textColor};
        border-color: ${({ theme }) => theme.lightBorderColor};
        padding: 8px 4px;
        font-weight: 600;
    }

    .rbc-month-view,
    .rbc-time-view {
        border-color: ${({ theme }) => theme.lightBorderColor};
    }

    .rbc-day-bg + .rbc-day-bg,
    .rbc-month-row + .rbc-month-row {
        border-color: ${({ theme }) => theme.lightBorderColor};
    }

    .rbc-off-range-bg {
        background-color: ${({ theme }) => theme.backgroundColor};
    }

    .rbc-date-cell {
        color: ${({ theme }) => theme.textColor};
        padding: 4px 8px;
    }

    .rbc-off-range {
        color: ${({ theme }) => theme.disabledTextColor || theme.lightBorderColor};
    }
`;
